import PDPHeader from '@library/images/common/pdp-header.svg'
import PDPFooter from '@library/images/common/pdp-footer.svg'
import '@fontsource/metropolis'
import '@fontsource/metropolis/500.css'
import PaymentIcon from '@library/components/payment-icon'
import { useEffect, useState, useRef } from 'react'
import styles from './simulator.module.css'
import { Skeleton } from 'gokwik-ui-kit'
import { PAYMENT_METHODS } from '@library/utilities/constants/constants'
import React from 'react'

interface PaymentMethod {
    name: string
    btnLabel: string
    eventName: string
    icon: string
    class: string
    subIcon: string
    enableFor: string
    prefix: string
    nonTouch: boolean
    alternateIcon?: string
}

const upiAppsData = [
    {
        name: 'phonepe',
        btnLabel: 'PhonePe',
        eventName: 'Phonepe',
        icon: 'phonepe-icon',
        class: '',
        subIcon: 'phonepe-icon',
        enableFor: 'All',
        prefix: 'phonepe://pay?',
        nonTouch: true,
        alternateIcon: 'phonepe-new-icon',
    },
    {
        name: 'tez',
        btnLabel: 'G Pay',
        eventName: 'Google Pay',
        icon: 'tez-icon',
        class: '',
        subIcon: 'tez-icon',
        enableFor: 'All',
        prefix: 'tez://upi/pay?',
        nonTouch: true,
        alternateIcon: 'tez-new-icon',
    },
    {
        name: 'paytm',
        btnLabel: 'Paytm',
        eventName: 'Paytm',
        icon: 'paytm-icon',
        class: '',
        subIcon: 'paytm-icon',
        enableFor: 'All',
        prefix: 'paytmmp://pay?',
        nonTouch: true,
        alternateIcon: 'paytm-new-icon',
    },
]
export interface PaymentMethodsObject {
    label?: string
    subtext?: string
    color?: string
    backgroundColor?: string
    name?: string
    badgeText?: string
    badgeColor?: string
    badgeBgColor?: string
    showPreferredPanel?: boolean
    preferredPanelApps?: PaymentMethod[]
    isEnabled?: boolean
    isPPCODEnabled?: boolean
    price?: number
    highlightMethod?: boolean
    fadeMethod?: boolean
}

export interface ExtensionScreenConfig {
    isVisible: boolean
    render: () => React.ReactNode
}

export interface SimulatorExtensions {
    customScreens?: {
        [key: string]: ExtensionScreenConfig
    }
    activeCustomScreenKey?: string
}

export interface SimulatorProps {
    paymentMethods: PaymentMethodsObject[]
    showComparedPrice?: boolean
    showPrice: boolean
    pageTitle: string
    pageTitleColor: string
    paymentButtonsStyle: string
    showDecimalValue: boolean
    methodPositions?: string[]
    extensions?: SimulatorExtensions
}

interface Props {
    simulatorInfo: SimulatorProps
}

const getSamplePrice = (price = 123, showDecimalValue = false) => {
    return showDecimalValue ? `₹${price}.10` : `₹${price}`
}

export default function Simulator({ simulatorInfo }: Props) {
    const [configData, setConfigData] = useState<SimulatorProps>()
    const [upiConfig, setUpiConfig] = useState<PaymentMethodsObject>()

    useEffect(() => {
        const upiConfig = simulatorInfo?.paymentMethods?.find((btn) => btn.name === 'upi')
        setUpiConfig(upiConfig)
        setConfigData(simulatorInfo)
    }, [simulatorInfo])

    const enabledMethods = configData?.paymentMethods?.filter((btn) => btn.isEnabled)
    const keepFooterFixed =
        (enabledMethods?.length < 6 &&
            (!upiConfig?.showPreferredPanel || (upiConfig?.showPreferredPanel && !upiConfig?.isEnabled))) ||
        (enabledMethods?.length < 3 && upiConfig?.showPreferredPanel)
    const highlightMethod = configData?.paymentMethods?.find((btn) => btn.highlightMethod)
    const showCustomScreen =
        configData?.extensions?.customScreens &&
        configData.extensions.activeCustomScreenKey &&
        configData.extensions.customScreens[configData.extensions.activeCustomScreenKey]?.isVisible

    return (
        <div className={styles.simulatorWrapper}>
            <div className={styles.previewContainer}>
                <h4 className={styles.previewTitle}>Preview</h4>
            </div>
            <div className={styles.simulatorContainer}>
                <div className={styles.contentWrapper}>
                    {highlightMethod && <div className='absolute inset-0 bg-black opacity-40 z-10' />}
                    <SimulatorHeader />
                    <div className={`${styles.simulatorBody} mb-0`}>
                        <Skeleton.Button block />
                        <Skeleton.Button block style={{ height: 80 }} />
                    </div>
                    {showCustomScreen ? (
                        <div
                            className={`${styles.fadeIn} ${configData.extensions.activeCustomScreenKey} ${styles.customScreen}`}
                        >
                            {configData.extensions.customScreens[configData.extensions.activeCustomScreenKey].render()}
                        </div>
                    ) : (
                        <div className={styles.simulatorBody}>
                            <div>
                                <h2 className={styles.paymentsHeader}>Payment Options</h2>
                                {configData?.pageTitle && (
                                    <p
                                        className={styles.paymentsSubHeader}
                                        style={{ color: configData?.pageTitleColor ?? '#000000' }}
                                    >
                                        {configData.pageTitle}
                                    </p>
                                )}
                            </div>
                            {upiConfig && upiConfig?.isEnabled && upiConfig?.showPreferredPanel && (
                                <CombinedPanel
                                    btn={upiConfig}
                                    configData={configData}
                                    btnBgColor={upiConfig?.backgroundColor}
                                    btnColor={upiConfig?.color}
                                />
                            )}
                            <div
                                className={`${styles.paymentBtnsContainer} ${
                                    configData?.paymentButtonsStyle === 'classic' ? 'gap-2' : 'rounded-lg bg-white pb-0'
                                }`}
                            >
                                {configData?.paymentMethods &&
                                    configData.paymentMethods.map(
                                        (btn) =>
                                            !(btn.name === 'upi' && upiConfig?.showPreferredPanel) && (
                                                <PaymentButton key={btn.name} btn={btn} configData={configData} />
                                            ),
                                    )}
                            </div>
                        </div>
                    )}
                    {<SimulatorFooter keepFooterFixed={keepFooterFixed} />}
                </div>
            </div>
        </div>
    )
}

function SimulatorHeader() {
    return <img className={styles.fixedHeader} src={PDPHeader} width={'100%'} />
}

function SimulatorFooter({ keepFooterFixed = false }) {
    const [isFooterFixed, setIsFooterFixed] = useState(false)
    useEffect(() => {
        setIsFooterFixed(keepFooterFixed)
    }, [keepFooterFixed])
    return (
        <img
            className={`${isFooterFixed ? `${styles.fixedFooter}` : `${styles.stickyFooter}`}`}
            src={PDPFooter}
            width={'100%'}
        />
    )
}

function Badge({
    badgeText,
    badgeBgColor,
    badgeColor,
    paymentButtonsStyle,
}: PaymentMethodsObject & { paymentButtonsStyle: string }) {
    return (
        <div className={paymentButtonsStyle !== 'classic' ? styles.btnDiscountBadge : styles.oldBtnDiscountBadge}>
            <div className={styles.left} style={{ backgroundColor: badgeBgColor }}></div>
            <div className={styles.badgeContent} style={{ backgroundColor: badgeBgColor }}>
                <div className={styles.badgeText} style={{ color: badgeColor }}>
                    {badgeText}
                </div>
            </div>
            <div className={styles.right} style={{ backgroundColor: badgeBgColor }}></div>
        </div>
    )
}

function PaymentButton({ btn, configData }: { btn: PaymentMethodsObject; configData: SimulatorProps }) {
    const buttonRef = useRef<HTMLButtonElement>(null)
    const scrollToButtonCondition = btn.highlightMethod || btn.fadeMethod

    useEffect(() => {
        if (scrollToButtonCondition && buttonRef.current) {
            buttonRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        }
    }, [scrollToButtonCondition])

    return (
        <button
            ref={buttonRef}
            key={btn.name}
            className={`${styles.paymentBtn} ${configData.paymentButtonsStyle !== 'classic' && styles.alternateUi} ${
                !btn?.isEnabled && 'hidden'
            }
                ${btn.highlightMethod ? styles.fadeIn : ''} ${btn.fadeMethod ? styles.animateFadeOut : ''}
            }`}
            style={{ backgroundColor: btn.backgroundColor, color: btn.color }}
        >
            <div className={styles.paymentButtonWrapper}>
                <div className={styles.paymentIconContainer}>
                    <PaymentIcon
                        fill={configData.paymentButtonsStyle !== 'classic' ? 'black' : btn.color}
                        className={`payment-btn-icon ${btn?.name}`}
                    />
                </div>
                <div className={styles.paymentText}>
                    <span>{btn.isPPCODEnabled ? 'PPCOD' : btn.label}</span>
                    {btn?.subtext && <span className={styles.subtext}>{btn.subtext}</span>}
                </div>
            </div>
            <div className={styles.priceWrapper}>
                <Price configData={configData} btn={btn} price={btn.price} />
            </div>
            {btn.badgeText && <Badge {...btn} paymentButtonsStyle={configData.paymentButtonsStyle} />}
        </button>
    )
}

function CombinedPanel({
    btn,
    configData,
    btnBgColor,
    btnColor,
}: {
    btn: PaymentMethodsObject
    configData: SimulatorProps
    btnBgColor: string
    btnColor: string
}) {
    return (
        <div className={`${styles.pspPanel} ${btn.highlightMethod && styles.fadeIn}`}>
            <section className={styles.pspPanelHeader}>
                <div className={styles.label}>
                    <div className={styles.icon}>
                        <PaymentIcon className='payment-btn-icon upi' fill={'black'} />
                    </div>
                    <span className={styles.title}>UPI</span>
                </div>
                <div className={styles.pspPriceContainer}>
                    <Price configData={configData} btn={btn} color={'black'} />
                </div>
                {btn?.badgeText && <Badge {...btn} paymentButtonsStyle={configData.paymentButtonsStyle} />}
            </section>
            <div className={styles.pspWrappers}>
                {upiAppsData?.map((payButton) => (
                    <button className={`${styles.pspBtn} ${styles[`${payButton.name}Bg`]}`} key={payButton.name}>
                        <div className={styles.borderView}>
                            <span className={styles.img}>
                                <span className={`${styles.spriteIcons} ${styles[payButton.icon]}`}></span>
                            </span>
                        </div>
                        <span>{payButton.btnLabel}</span>
                    </button>
                ))}
            </div>
            <div className={styles.orOption}>
                <p>OR</p>
            </div>
            <div className={styles.collectWrapper}>
                <UpiCollect btnBgColor={btnBgColor} btnColor={btnColor} />
            </div>
        </div>
    )
}
function Price({
    configData,
    btn,
    color,
    price,
}: {
    configData: SimulatorProps
    btn: PaymentMethodsObject
    color?: string
    price?: number
}) {
    return (
        <>
            {[PAYMENT_METHODS.GOKWIK_BNPL, PAYMENT_METHODS.SNAPMINT].includes(btn.name) ? (
                <div className={styles.bnplPrice}>
                    <span className={styles.bnplSubtext}>&#8377;10 Now</span>
                    <span className={styles.bnplEmiSubtext}>+2 EMIs</span>
                </div>
            ) : (
                (configData?.showPrice || [PAYMENT_METHODS.PPCOD, PAYMENT_METHODS.SIMPLPAYIN3].includes(btn.name)) && (
                    <div className={styles.priceComparison}>
                        {configData?.showComparedPrice && (
                            <span className={styles.strikethrough}>
                                {getSamplePrice(price ?? 124, configData?.showDecimalValue)}
                            </span>
                        )}
                        <span>{getSamplePrice(price ?? 123, configData?.showDecimalValue)}</span>
                    </div>
                )
            )}
            <div className={styles.paymentIcon}>
                <PaymentIcon
                    fill={configData.paymentButtonsStyle !== 'classic' ? 'black' : color || btn.color}
                    className={`payment-btn-icon arrow`}
                />
            </div>
        </>
    )
}
function UpiCollect({ btnBgColor, btnColor }: { btnBgColor: string; btnColor: string }) {
    return (
        <section className={styles.collectWrapper}>
            <div className={styles.customInput}>
                <label htmlFor='vpaId' className={styles.text}>
                    Pay via UPI ID
                </label>
                <input type='text' className={styles.input} placeholder='Enter your UPI ID' />
                <button className={styles.collectBtn} style={{ backgroundColor: btnBgColor, color: btnColor }}>
                    Pay
                </button>
            </div>
        </section>
    )
}
